/**
 * Cached map of `{ [speaker_name]: { image, photo_count } }`.
 *
 * Populated lazily by hitting `/api/speakers` once per page load. Consumers
 * subscribe with `useSpeakerPortrait(name)` and get the URL back synchronously
 * once the fetch resolves; the store re-renders subscribed components.
 *
 * Feb-2026 RULE 19: also expose an `image_original` URL — the pre-rembg
 * (non-translucent) portrait. Recording cards rendered OUTSIDE a speaker's
 * own /speaker page fall back to this original when the speaker has no
 * gallery-folder of pics, per the user's explicit rule.
 */
import { useEffect, useState } from 'react';
import { getSpeakers } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const LS_KEY = 'vaad_speakers_map_v3';
const seed = (() => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
})();

const state = { map: seed || null, inFlight: null };
const subscribers = new Set();

const notify = () => subscribers.forEach((cb) => cb());

/**
 * Given a cutout URL (`/api/portrait/cutout/{fid}?size=...`), return the
 * ORIGINAL (non-translucent) portrait URL. If we can't parse the file_id
 * out of the URL, return the input unchanged.
 */
export const originalFromCutout = (url) => {
  if (!url || typeof url !== 'string') return url;
  const m = url.match(/\/api\/portrait\/cutout\/([A-Za-z0-9_-]+)/);
  if (!m) return url;
  // Strip any existing query so we don't inherit ?size=/?v= from the cutout.
  const origin = url.includes('://')
    ? url.slice(0, url.indexOf('/api/portrait/'))
    : '';
  // ?size=400 — backend `_serve_resized` never resizes cutouts, so this
  // stays Rule-16 safe while shipping 5-15 KB thumbs instead of 60-90 KB
  // originals (Feb-2026 "website so slow" pass).
  return `${origin}/api/portrait/by-file-id/${m[1]}?size=400`;
};

const loadOnce = () => {
  if (state.inFlight) return state.inFlight;
  state.inFlight = (async () => {
    try {
      const list = await getSpeakers();
      const map = {};
      for (const s of list) {
        if (!s?.name || !s?.image || typeof s.image !== 'string') continue;
        const abs = s.image.startsWith('http')
          ? s.image
          : s.image.startsWith('/')
            ? `${API}${s.image}`
            : null;
        if (!abs) continue;
        map[s.name] = {
          image: abs,
          image_original: originalFromCutout(abs),
          photo_count: Number.isFinite(s.photo_count) ? s.photo_count : 0,
        };
      }
      state.map = map;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(LS_KEY, JSON.stringify(map));
        }
      } catch { /* quota */ }
    } catch {
      if (!state.map) state.map = {};
    } finally {
      state.inFlight = null;
      state.sortedKeys = null;
      notify();
    }
    return state.map;
  })();
  return state.inFlight;
};

export const invalidateSpeakerPortraits = () => {
  state.map = null;
  state.inFlight = null;
  state.sortedKeys = null;
  notify();
};
export const invalidateSpeakerPortraitsCache = invalidateSpeakerPortraits;

const getSortedKeys = () => {
  if (!state.map) return [];
  if (!state.sortedKeys) {
    state.sortedKeys = Object.keys(state.map).sort((a, b) => b.length - a.length);
  }
  return state.sortedKeys;
};

export function findSpeakerInTitle(title) {
  if (!title || !state.map) return null;
  const hay = ` ${title.toLowerCase()} `;
  for (const name of getSortedKeys()) {
    const needle = name.toLowerCase();
    if (needle.length < 6) continue;
    if (hay.includes(needle)) return name;
  }
  return null;
}

/** Returns { image, image_original, photo_count } for a speaker, or null. */
export const getSpeakerEntry = (name) => (state.map ? state.map[name] || null : null);

/** True iff we know the speaker exists and has zero gallery photos. */
export const speakerIsGalleryless = (name) => {
  const e = getSpeakerEntry(name);
  return !!e && (e.photo_count || 0) === 0;
};

const pickUrl = (entry, preferOriginal) => {
  if (!entry) return null;
  if (preferOriginal && (entry.photo_count || 0) === 0 && entry.image_original) {
    return entry.image_original;
  }
  return entry.image;
};

export default function useSpeakerPortrait(speakerName, opts = {}) {
  const preferOriginal = !!opts.preferOriginalIfNoGallery;
  const [url, setUrl] = useState(() => pickUrl(getSpeakerEntry(speakerName), preferOriginal));
  useEffect(() => {
    let alive = true;
    const update = () => { if (alive) setUrl(pickUrl(getSpeakerEntry(speakerName), preferOriginal)); };
    subscribers.add(update);
    loadOnce().then(update);
    return () => { alive = false; subscribers.delete(update); };
  }, [speakerName, preferOriginal]);
  return url;
}

/**
 * Resolve the best portrait for a recording (see original docstring).
 *
 * `opts.preferOriginalIfNoGallery` (default false): when true AND the
 * resolved speaker has zero gallery photos, return the pre-rembg original
 * portrait instead of the translucent cutout. RULE 19: RecordingCard opts
 * in whenever it is rendered OUTSIDE the speaker's own /speaker page.
 */
export function useResolvedPortrait(recording, opts = {}) {
  const preferOriginal = !!opts.preferOriginalIfNoGallery;
  const speakerName = recording?.speaker || null;
  const title = recording?.title || '';
  const resolve = () => {
    if (!state.map) return null;
    if (speakerName && state.map[speakerName]) {
      return pickUrl(state.map[speakerName], preferOriginal);
    }
    const inferred = findSpeakerInTitle(title);
    if (inferred && state.map[inferred]) {
      return pickUrl(state.map[inferred], preferOriginal);
    }
    return null;
  };
  const [url, setUrl] = useState(resolve);
  useEffect(() => {
    let alive = true;
    const update = () => { if (alive) setUrl(resolve()); };
    subscribers.add(update);
    loadOnce().then(update);
    return () => { alive = false; subscribers.delete(update); };
  }, [speakerName, title, preferOriginal]);
  return url;
}
